import {
  DndContext,
  closestCenter,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Modal } from "flowbite-react";
import { useEffect, useState } from "react";
import { RxDragHandleDots2 } from "react-icons/rx";
import axios from "../../config/axiosInstance";
import { ADMIN } from "../../config/apiConfig";

// ITEM DRAGGABLE
const SortableItem = ({ id, containerId, name, isPrincipal = false }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    data: { containerId },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="border p-2 rounded flex items-center justify-between bg-white shadow"
    >
      <div className="flex items-center gap-2">
        <span>{name || id}</span>
        {isPrincipal && (
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md font-medium">
            Principal
          </span>
        )}
      </div>
      <button {...attributes} {...listeners}>
        <RxDragHandleDots2 className="cursor-move" />
      </button>
    </li>
  );
};

// CONTAINER VISUAL COM EFEITO AO HOVER
const DropZone = ({
  id,
  title,
  items,
  onRenderItem,
  isEmptyText,
  isDashed,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id });

  const containerStyle = isDashed
    ? `border-2 ${
        isOver ? "border-green-400 bg-green-50" : "border-dashed bg-gray-100"
      }`
    : `border ${
        isOver ? "border-green-400 bg-green-50" : "bg-white"
      } shadow-sm`;

  return (
    <div
      ref={setNodeRef}
      className={`rounded-md p-4 transition-all duration-200 min-h-[200px] ${containerStyle}`}
    >
      <h4 className="text-md font-semibold mb-3 text-gray-800">{title}</h4>
      <ul className="space-y-2">
        {items.length > 0 ? (
          items.map((id, index) => onRenderItem(id, index))
        ) : (
          <li className="text-sm text-gray-400 italic">{isEmptyText}</li>
        )}
      </ul>
    </div>
  );
};

// MODAL PRINCIPAL
export const ModalRedudancy = ({
  isOpen,
  onClose,
  title = "Editar ordem",
  queryId,
}) => {
  const [activeItems, setActiveItems] = useState([]);
  const [inactiveItems, setInactiveItems] = useState([]);
  const [providerMap, setProviderMap] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!queryId) return;

    const fetchProviders = async () => {
      try {
        const { data } = await axios.get(`${ADMIN.GET_PROVIDERS}/${queryId}`);

        const active = data.data
          .filter((item) => item.priority > 0)
          .sort((a, b) => a.priority - b.priority)
          .map((item) => item.providerId);

        const inactive = data.data
          .filter((item) => item.priority === 0)
          .map((item) => item.providerId);

        const map = Object.fromEntries(
          data.data.map((item) => [item.providerId, item.provider.name])
        );

        setActiveItems(active);
        setInactiveItems(inactive);
        setProviderMap(map);
      } catch (err) {
        console.error("Erro ao buscar provedores:", err);
      }
    };

    fetchProviders();
  }, [queryId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  return (
    <Modal show={isOpen} onClose={onClose} size="4xl">
      <Modal.Header>{title}</Modal.Header>
      <Modal.Body>
        <p className="text-sm text-gray-600 mb-4">
          Reorganize os insumos de redundância arrastando os itens para cima ou
          para baixo. Você também pode mover um item para a área de "Não
          considerar" para ignorá-lo durante a consulta.
        </p>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={({ active, over }) => {
            if (!over) return;

            const activeId = active.id;
            const fromContainer = active.data.current?.containerId;

            // Tenta pegar o container de destino pelo data do item
            const toContainer = over.data?.current?.containerId || over.id;

            if (!fromContainer || !toContainer) return;

            // Mesma lista: reordenar
            if (fromContainer === toContainer) {
              if (toContainer === "active-dropzone") {
                setActiveItems((items) =>
                  arrayMove(
                    items,
                    items.indexOf(activeId),
                    items.indexOf(over.id)
                  )
                );
              } else if (toContainer === "inactive-dropzone") {
                setInactiveItems((items) =>
                  arrayMove(
                    items,
                    items.indexOf(activeId),
                    items.indexOf(over.id)
                  )
                );
              }
            }

            // De ativo para inativo
            else if (
              fromContainer === "active-dropzone" &&
              toContainer === "inactive-dropzone"
            ) {
              setActiveItems((prev) =>
                prev.filter((item) => item !== activeId)
              );
              setInactiveItems((prev) => [...prev, activeId]);
            }

            // De inativo para ativo
            else if (
              fromContainer === "inactive-dropzone" &&
              toContainer === "active-dropzone"
            ) {
              setInactiveItems((prev) =>
                prev.filter((item) => item !== activeId)
              );
              setActiveItems((prev) => [...prev, activeId]);
            }
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Ordem dos Insumos */}
            <SortableContext
              items={activeItems}
              id="active-dropzone"
              strategy={verticalListSortingStrategy}
            >
              <DropZone
                id="active-dropzone"
                title="Ordem dos Provedores"
                items={activeItems}
                onRenderItem={(id, index) => (
                  <SortableItem
                    key={id}
                    id={id}
                    name={providerMap[id]}
                    containerId="active-dropzone"
                    isPrincipal={index === 0}
                  />
                )}
                isEmptyText="Nenhum item ativo"
              />
            </SortableContext>

            {/* Não Considerar */}
            <SortableContext
              items={inactiveItems}
              id="inactive-dropzone"
              strategy={verticalListSortingStrategy}
            >
              <DropZone
                id="inactive-dropzone"
                title="Não considerar"
                items={inactiveItems}
                onRenderItem={(id) => (
                  <SortableItem
                    key={id}
                    id={id}
                    name={providerMap[id]}
                    containerId="inactive-dropzone"
                  />
                )}
                isEmptyText="Arraste aqui os itens a ignorar"
                isDashed
              />
            </SortableContext>
          </div>
        </DndContext>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex gap-2 items-center justify-center w-full">
          <Button
            className="px-4"
            onClick={async () => {
              setIsSaving(true);

              const body = [
                ...activeItems.map((providerId, index) => ({
                  queryId,
                  providerId,
                  priority: index + 1,
                })),
                ...inactiveItems.map((providerId) => ({
                  queryId,
                  providerId,
                  priority: 0,
                })),
              ];

              try {
                await axios.put(ADMIN.UPDATE_REDUDANCY, body);
                onClose(); // ou exibir toast
              } catch (err) {
                console.error("Erro ao salvar:", err);
              } finally {
                setIsSaving(false);
              }
            }}
            disabled={isSaving}
          >
            {isSaving ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Salvando...
              </div>
            ) : (
              "Salvar"
            )}
          </Button>

          <Button onClick={onClose} className="bg-gray-500 px-4">
            Fechar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
